import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";

export default function CartDrawer() {
    const { items, total, count, open, setOpen, removeItem, updateQty, clear } =
        useCart();
    const [checkingOut, setCheckingOut] = useState(false);

    const checkout = async () => {
        if (items.length === 0) return;
        setCheckingOut(true);
        try {
            const res = await api.post("/checkout/cart", {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    quantity: i.quantity,
                    size: i.size,
                })),
                origin_url: window.location.origin,
            });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                toast.error("Checkout failed");
                setCheckingOut(false);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Checkout failed");
            setCheckingOut(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
                        data-testid="cart-overlay"
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#0A0A0A] border-l border-white/10 z-[61] flex flex-col"
                        data-testid="cart-drawer"
                    >
                        <header className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <ShoppingBag size={16} />
                                <p className="font-mono text-xs uppercase tracking-[0.25em]">
                                    Bag · {count}
                                </p>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="cart-close"
                                className="p-1 hover:bg-white/10"
                                aria-label="Close cart"
                            >
                                <X size={18} />
                            </button>
                        </header>

                        <div className="flex-1 overflow-y-auto px-6 py-6 hide-scrollbar">
                            {items.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                                    <p className="label-mono">— Empty</p>
                                    <h3 className="font-display text-4xl uppercase tracking-tight mt-4">
                                        Your bag
                                        <br />
                                        <span className="italic">is empty.</span>
                                    </h3>
                                    <p className="text-muted-foreground mt-3 text-sm">
                                        Add a piece to begin.
                                    </p>
                                </div>
                            ) : (
                                <ul className="space-y-6">
                                    {items.map((i) => (
                                        <li
                                            key={`${i.product_id}-${i.size}`}
                                            className="flex gap-4 border-b border-white/5 pb-6 last:border-0"
                                            data-testid={`cart-item-${i.product_id}-${i.size}`}
                                        >
                                            <div className="w-20 h-24 product-photo overflow-hidden shrink-0">
                                                <img
                                                    src={i.image_url}
                                                    alt={i.name}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <h4 className="font-sans uppercase text-xs tracking-wide truncate">
                                                            {i.name}
                                                        </h4>
                                                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                                                            Size · {i.size}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() =>
                                                            removeItem(i.product_id, i.size)
                                                        }
                                                        data-testid={`remove-${i.product_id}-${i.size}`}
                                                        className="p-1 text-muted-foreground hover:text-signal"
                                                        aria-label="Remove"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>

                                                <div className="mt-3 flex items-center justify-between gap-4">
                                                    <div className="inline-flex items-center border border-white/15">
                                                        <button
                                                            onClick={() =>
                                                                updateQty(
                                                                    i.product_id,
                                                                    i.size,
                                                                    i.quantity - 1
                                                                )
                                                            }
                                                            data-testid={`dec-${i.product_id}-${i.size}`}
                                                            className="px-2 py-1 hover:bg-white/10"
                                                            aria-label="Decrease"
                                                        >
                                                            <Minus size={12} />
                                                        </button>
                                                        <span className="px-3 font-mono text-xs">
                                                            {i.quantity}
                                                        </span>
                                                        <button
                                                            onClick={() =>
                                                                updateQty(
                                                                    i.product_id,
                                                                    i.size,
                                                                    i.quantity + 1
                                                                )
                                                            }
                                                            data-testid={`inc-${i.product_id}-${i.size}`}
                                                            className="px-2 py-1 hover:bg-white/10"
                                                            aria-label="Increase"
                                                        >
                                                            <Plus size={12} />
                                                        </button>
                                                    </div>
                                                    <p className="font-display text-lg">
                                                        ${(i.price * i.quantity).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <footer className="border-t border-white/10 px-6 py-6 space-y-4">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>Subtotal</span>
                                    <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
                                </div>
                                <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>Shipping (flat)</span>
                                    <span data-testid="cart-shipping">
                                        {items.length > 0 ? "$11.95" : "—"}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between border-t border-white/10 pt-4">
                                <span className="label-mono">Total</span>
                                <span
                                    className="font-display text-3xl"
                                    data-testid="cart-total"
                                >
                                    ${(items.length > 0 ? total + 11.95 : 0).toFixed(2)}
                                </span>
                            </div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                Free returns within 30 days
                            </p>
                            <button
                                onClick={checkout}
                                disabled={items.length === 0 || checkingOut}
                                data-testid="cart-checkout"
                                className="w-full bg-white text-black py-4 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-white/85 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                            >
                                {checkingOut ? "Redirecting…" : "Secure Checkout"}
                                <ArrowRight size={14} />
                            </button>
                            {items.length > 0 && (
                                <button
                                    onClick={clear}
                                    className="w-full text-muted-foreground hover:text-white py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
                                >
                                    Clear bag
                                </button>
                            )}
                        </footer>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
